import { BitbucketRepositoryAri } from '@atlassian/ari/bitbucket';

export type DevDocsSourceProvider = 'bitbucket' | 'github';

export type DevDocsScmType = 'BITBUCKET' | 'GITHUB';

export type DevDocsRepositoryParts = {
	owner: string;
	repo: string;
};

export type DevDocsObjectResolverAriInput = {
	objectId?: string;
};

export type DevDocsObjectResolverProviderMetadata = {
	key?: string | null;
	product?: string | null;
	resourceType?: string | null;
};

export type DevDocsRepositoryRootUrl = {
	host: string;
	repository: DevDocsRepositoryParts;
	url: string;
};

export type DevDocsSourceProviderUrlContext = {
	environment?: string | null;
};

type DevDocsSourceProviderValue = string | null | undefined;

export type DevDocsSourceProviderConfig = {
	buildObjectResolverRepositoryAri?: (input: DevDocsObjectResolverAriInput) => string | undefined;
	buildCanonicalUrl: (
		repository: DevDocsRepositoryParts,
		context?: DevDocsSourceProviderUrlContext,
	) => string;
	canonicalHost: string;
	displayName: string;
	objectResolverProviderKey: string;
	provider: DevDocsSourceProvider;
	repositoryAriPattern: RegExp;
	scmType: DevDocsScmType;
};

const GITHUB_REPOSITORY_SHORTHAND_PATTERN = /^([A-Za-z0-9_.-]+)\/([A-Za-z0-9_.-]+(?:\.git)?)$/;
const BITBUCKET_INTEGRATION_HOST = 'integration.bb-inf.net';
const REPOSITORY_RESOURCE_TYPE = 'repository';

const buildRepositoryUrl = (host: string, { owner, repo }: DevDocsRepositoryParts): string =>
	`https://${host}/${owner}/${repo}`;

const getBitbucketHost = (environment?: string | null): string =>
	environment === 'STAGING' ? BITBUCKET_INTEGRATION_HOST : 'bitbucket.org';

const stripGitSuffix = (value: string): string => value.replace(/\.git$/i, '');
const stripUuidBraces = (value: string): string => value.replace(/^\{(.+)\}$/, '$1');

const buildBitbucketRepositoryAri = ({
	objectId,
}: DevDocsObjectResolverAriInput): string | undefined => {
	if (!objectId) {
		return undefined;
	}

	try {
		return BitbucketRepositoryAri.create({
			repositoryId: stripUuidBraces(objectId),
		}).toString();
	} catch {
		return undefined;
	}
};

export const getRepositoryPartsFromPathParts = (
	pathParts: string[],
): DevDocsRepositoryParts | undefined => {
	if (pathParts.length < 2) {
		return undefined;
	}

	const [owner, repoPath] = pathParts;
	const repo = repoPath ? stripGitSuffix(repoPath) : undefined;

	return owner && repo ? { owner, repo } : undefined;
};

export const getGithubRepositoryPartsFromShorthand = (
	value: string,
): DevDocsRepositoryParts | undefined => {
	const repositoryShorthand = value.match(GITHUB_REPOSITORY_SHORTHAND_PATTERN);
	if (!repositoryShorthand) {
		return undefined;
	}

	const [, owner, repoPath] = repositoryShorthand;
	const repo = repoPath ? stripGitSuffix(repoPath) : undefined;

	return owner && repo ? { owner, repo } : undefined;
};

export const getDevDocsNormalizedSourceProviderHost = (host: string): string =>
	host.toLowerCase().replace(/^www\./, '');

export const buildDevDocsRepositoryRootUrl = (
	repository: DevDocsRepositoryParts,
	host: string,
): string => buildRepositoryUrl(host, repository);

export const getDevDocsRepositoryRootHost = (
	providerConfig: DevDocsSourceProviderConfig,
	host?: string | null,
): string => {
	const normalizedHost = host ? getDevDocsNormalizedSourceProviderHost(host) : null;

	return providerConfig.provider === 'bitbucket' && normalizedHost === BITBUCKET_INTEGRATION_HOST
		? normalizedHost
		: providerConfig.canonicalHost;
};

export const getDevDocsRepositoryRootUrl = (
	value?: string | null,
): DevDocsRepositoryRootUrl | undefined => {
	if (!value) {
		return undefined;
	}

	let parsedUrl: URL;

	try {
		parsedUrl = new URL(value);
	} catch {
		return undefined;
	}

	const repository = getRepositoryPartsFromPathParts(parsedUrl.pathname.split('/').filter(Boolean));
	if (!repository) {
		return undefined;
	}

	const host = getDevDocsNormalizedSourceProviderHost(parsedUrl.hostname);

	return {
		host,
		repository,
		url: buildDevDocsRepositoryRootUrl(repository, host),
	};
};

export const DEV_DOCS_SOURCE_PROVIDER_CONFIGS: readonly DevDocsSourceProviderConfig[] = [
	{
		buildCanonicalUrl: (repository, { environment } = {}) =>
			buildRepositoryUrl(getBitbucketHost(environment), repository),
		buildObjectResolverRepositoryAri: buildBitbucketRepositoryAri,
		canonicalHost: 'bitbucket.org',
		displayName: 'Bitbucket',
		objectResolverProviderKey: 'integration-bitbucket-object-provider',
		provider: 'bitbucket',
		repositoryAriPattern:
			/^ari:cloud:bitbucket::repository\/[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/,
		scmType: 'BITBUCKET',
	},
	{
		buildCanonicalUrl: (repository) => buildRepositoryUrl('github.com', repository),
		canonicalHost: 'github.com',
		displayName: 'GitHub',
		objectResolverProviderKey: 'github-object-provider',
		provider: 'github',
		repositoryAriPattern: /^ari:third-party:github\.github::repository\/[0-9]+$/,
		scmType: 'GITHUB',
	},
];

export const getDevDocsSourceProviderByHost = (
	host: string,
): DevDocsSourceProviderConfig | undefined => {
	const normalizedHost = getDevDocsNormalizedSourceProviderHost(host);

	return DEV_DOCS_SOURCE_PROVIDER_CONFIGS.find(
		(providerConfig) =>
			providerConfig.canonicalHost === normalizedHost ||
			(providerConfig.provider === 'bitbucket' && normalizedHost === BITBUCKET_INTEGRATION_HOST),
	);
};

const normalizeProviderValue = (value: DevDocsSourceProviderValue): string | null => {
	const normalizedValue = value?.toLowerCase().replace(/\s+/g, '').trim();
	return normalizedValue || null;
};

const getHostFromProviderValue = (value: string): string | null => {
	try {
		return new URL(value).hostname;
	} catch {
		try {
			return new URL(`https://${value}`).hostname;
		} catch {
			return null;
		}
	}
};

export const getDevDocsSourceProviderConfigFromValue = (
	value: DevDocsSourceProviderValue,
): DevDocsSourceProviderConfig | undefined => {
	const normalizedValue = normalizeProviderValue(value);

	if (!normalizedValue) {
		return undefined;
	}

	const trimmedValue = value?.trim() ?? '';
	const hostProviderConfig = getDevDocsSourceProviderByHost(
		getHostFromProviderValue(trimmedValue) ?? '',
	);
	if (hostProviderConfig) {
		return hostProviderConfig;
	}

	return DEV_DOCS_SOURCE_PROVIDER_CONFIGS.find((providerConfig) => {
		const providerIdentifiers = [
			providerConfig.provider,
			providerConfig.displayName,
			providerConfig.canonicalHost,
			providerConfig.objectResolverProviderKey,
			providerConfig.scmType,
		];

		return (
			providerConfig.repositoryAriPattern.test(trimmedValue) ||
			providerIdentifiers.some((providerIdentifier) => {
				const normalizedIdentifier = normalizeProviderValue(providerIdentifier);
				return normalizedIdentifier ? normalizedValue === normalizedIdentifier : false;
			})
		);
	});
};

export const getDevDocsSourceProviderFromValue = (
	value: DevDocsSourceProviderValue,
): DevDocsSourceProvider | null => getDevDocsSourceProviderConfigFromValue(value)?.provider ?? null;

export const getDevDocsSourceProviderConfigFromObjectResolverMetadata = (
	metadata?: DevDocsObjectResolverProviderMetadata | null,
): DevDocsSourceProviderConfig | undefined => {
	const key = metadata?.key?.toLowerCase();
	const product = metadata?.product?.toLowerCase();
	const resourceType = metadata?.resourceType?.toLowerCase();

	return DEV_DOCS_SOURCE_PROVIDER_CONFIGS.find((providerConfig) => {
		const provider = providerConfig.provider.toLowerCase();
		const displayName = providerConfig.displayName.toLowerCase();

		return (
			key === providerConfig.objectResolverProviderKey.toLowerCase() ||
			product === provider ||
			product === displayName ||
			(resourceType === REPOSITORY_RESOURCE_TYPE && (key === provider || key === displayName))
		);
	});
};

export const getDevDocsSourceProviderForAppName = (
	appName: DevDocsSourceProviderValue,
): DevDocsSourceProvider | null => getDevDocsSourceProviderFromValue(appName);

export const isDevDocsSmartFolder = (containerApp: DevDocsSourceProviderValue): boolean =>
	getDevDocsSourceProviderForAppName(containerApp) !== null;

export const getDevDocsSourceProviderDisplayName = (
	value: DevDocsSourceProviderValue,
): string | null =>
	getDevDocsSourceProviderConfigFromValue(value)?.displayName ?? (value?.trim() || null);

export const getDevDocsSourceProviderConfig = (
	provider: DevDocsSourceProvider,
): DevDocsSourceProviderConfig => {
	const providerConfig = DEV_DOCS_SOURCE_PROVIDER_CONFIGS.find(
		(sourceProviderConfig) => sourceProviderConfig.provider === provider,
	);

	if (!providerConfig) {
		throw new Error(`Missing Dev Docs source provider config for ${provider}`);
	}

	return providerConfig;
};
